import httpClient from './httpClient';
import { BaseService } from './baseService';
import { displayCustomerName } from '../constants/customerType';
import { parseApiError } from '../utils/errorHandler';
import { MAX_DELIVERY_PAGE_SIZE, validateDeliveryDateRange } from '../utils/deliveryDateRange';

/** Campos aceitos em `sort` pela API; qualquer outro cai no padrão `invoiceId`. */
const SORTABLE_FIELDS = [
    'invoiceId',
    'customerId',
    'amount',
    'state',
    'dayToCharge',
    'creationDateTime',
];

/**
 * Monta a query string de GET /api/faturas.
 * @param {Object} params
 * @returns {URLSearchParams}
 */
export function buildInvoiceListParams({
    page = 0,
    size = 20,
    sort = 'invoiceId,desc',
    customerId,
    invoiceId,
    state,
    startDate,
    endDate,
} = {}) {
    if (startDate && endDate) {
        const validation = validateDeliveryDateRange(startDate, endDate);
        if (!validation.valid) {
            throw new Error(validation.message);
        }
    }

    const [field, direction] = String(sort || 'invoiceId,desc').split(',');
    const apiField = SORTABLE_FIELDS.includes(field?.trim()) ? field.trim() : 'invoiceId';
    const apiSort = `${apiField},${(direction || 'desc').trim().toLowerCase()}`;
    const cappedSize = Math.min(Math.max(1, Number(size) || 20), MAX_DELIVERY_PAGE_SIZE);

    const params = new URLSearchParams();
    params.set('page', String(Math.max(0, Number(page) || 0)));
    params.set('size', String(cappedSize));
    params.set('sort', apiSort);

    if (customerId != null && String(customerId).trim() !== '') {
        params.set('customerId', String(customerId).trim());
    }
    if (invoiceId != null && String(invoiceId).trim() !== '') {
        params.set('invoiceId', String(invoiceId).trim());
    }
    const states = Array.isArray(state) ? state : state ? [state] : [];
    states.filter(Boolean).forEach((stateValue) => {
        params.append('state', String(stateValue));
    });
    if (startDate) {
        params.set('startDate', startDate);
    }
    if (endDate) {
        params.set('endDate', endDate);
    }

    return params;
}

/**
 * Mapeia um InvoiceHeader da API para o formato usado na listagem.
 * Nomes vêm em maiúsculas do backend; fantasia só é usada quando preenchida (tipicamente PJ).
 */
export function mapInvoiceHeader(item = {}) {
    const fantasyName =
        item.customerFantasyName != null && String(item.customerFantasyName).trim() !== ''
            ? String(item.customerFantasyName).trim()
            : '';
    return {
        invoiceId: item.invoiceId,
        customerId: item.customerId ?? null,
        customerType: item.customerType ?? null,
        customerName: displayCustomerName(fantasyName || item.customerName),
        amount: item.amount ?? 0,
        state: item.state ?? null,
        dayToCharge: item.dayToCharge || null,
        creationDateTime: item.creationDateTime || null,
    };
}

/**
 * Normaliza PagedInvoiceHeaders para o formato consumido pelo useCrud.
 * @param {{ content?: Array, page?: Object }} json
 */
export function parseInvoicePagedResponse(json = {}) {
    const content = (json.content || []).map(mapInvoiceHeader);
    const pageMeta = json.page || {};
    const totalPages = pageMeta.totalPages ?? 0;
    const pageNumber = Math.max(0, pageMeta.number ?? 0);

    return {
        content,
        page: {
            number: pageNumber,
            size: pageMeta.size ?? content.length,
            totalElements: pageMeta.totalElements ?? content.length,
            totalPages,
            first: totalPages === 0 || pageNumber === 0,
            last: totalPages === 0 || pageNumber >= totalPages - 1,
        },
    };
}

class InvoiceService extends BaseService {
    constructor() {
        super('/api/faturas');
    }

    /**
     * Lista faturas (GET /api/faturas).
     * @param {Object} params
     * @param {number} [params.page] - Página (0-based)
     * @param {number} [params.size] - Tamanho da página (máx. 30)
     * @param {string} [params.sort] - Ordenação (ex.: "invoiceId,desc")
     * @param {string|number|null} [params.customerId] - Filtrar por cliente
     * @param {string|number|null} [params.invoiceId] - Número exato da fatura
     * @param {string|string[]|null} [params.state] - Estado(s) da fatura (InvoiceStateEnum)
     * @param {string|null} [params.startDate] - Início do intervalo de criação (yyyy-MM-dd)
     * @param {string|null} [params.endDate] - Fim do intervalo de criação (yyyy-MM-dd)
     * @returns {Promise<{ content: Array, page: Object }>}
     */
    async list(params = {}) {
        const query = buildInvoiceListParams(params);
        const response = await httpClient.get(`${this.endpoint}?${query.toString()}`);
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return parseInvoicePagedResponse(await response.json());
    }

    /**
     * @returns {Promise<Object>} DeliveryInvoiceDTO
     */
    async getById(invoiceId) {
        return this.get(invoiceId);
    }

    /**
     * @param {{ customerId: number, dayToCharge?: string }} data
     * @returns {Promise<Object>} DeliveryInvoiceDTO
     */
    async createInvoice(data) {
        const payload = { customerId: Number(data.customerId) };
        if (data.dayToCharge) {
            payload.dayToCharge = data.dayToCharge;
        }
        const response = await httpClient.post(this.endpoint, payload);
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }

    /**
     * @param {number|string} invoiceId
     * @param {number[]} deliveryIds
     * @returns {Promise<{ invoice: Object, includedDeliveryIds: number[], omittedDeliveryIds: number[] }>}
     */
    async addCharges(invoiceId, deliveryIds) {
        const response = await httpClient.post(
            `${this.endpoint}/${encodeURIComponent(invoiceId)}/cobrancas`,
            { deliveryIds: deliveryIds.map(Number) },
        );
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }

    /**
     * @returns {Promise<Object>} DeliveryInvoiceDTO
     */
    async removeCharge(invoiceId, billableId) {
        const response = await httpClient.delete(
            `${this.endpoint}/${encodeURIComponent(invoiceId)}/cobrancas/${encodeURIComponent(billableId)}`,
        );
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }

    /**
     * @returns {Promise<Object>} DeliveryInvoiceDTO
     */
    async updateDraft(invoiceId, { dayToCharge }) {
        const response = await httpClient.patch(`${this.endpoint}/${encodeURIComponent(invoiceId)}`, {
            dayToCharge,
        });
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }

    /**
     * @returns {Promise<Object>} DeliveryInvoiceDTO
     */
    async confirm(invoiceId) {
        const response = await httpClient.patch(
            `${this.endpoint}/${encodeURIComponent(invoiceId)}/confirmacao`,
            null,
        );
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }

    /**
     * @param {string} action - InvoiceStateAction
     * @param {string} [occurredAt] - ISO-8601 datetime
     * @returns {Promise<Object>} DeliveryInvoiceDTO
     */
    async updateState(invoiceId, { action, occurredAt }) {
        const body = { action };
        if (occurredAt) {
            body.occurredAt = occurredAt;
        }
        const response = await httpClient.patch(
            `${this.endpoint}/${encodeURIComponent(invoiceId)}/estado`,
            body,
        );
        if (!response.ok) {
            throw await parseApiError(response);
        }
        return response.json();
    }
}

const invoiceService = new InvoiceService();
export default invoiceService;
